<template>
  <div class="db-tw-mb-9">
    <div class="db-tw-mb-9 md:db-tw-mb-5 db-tw-grid db-tw-grid-cols-2 db-tw-gap-3">
      <div>
        <DbInput
          :modelValue="totalBudget"
          :inputAttrs="{
            type: 'number',
            min: 0,
            required: isMandatory,
          }"
          v-on:update:model-value="$emit('update:totalBudget', $event)"
        >
          {{ labelTotalBudget }}
          <template
            v-if="v$.fieldBudget.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldBudget.$errors" />
          </template>
          <template
            v-if="currencySign"
            v-slot:prepend
          >
            {{ currencySign }}
          </template>
        </DbInput>
      </div>
      <div>
        <DbSelectCustom
          v-if="showCurrencySelect"
          :modelValue="currency"
          :options="optionsWithEmptyValue"
          :inputAttrs="{ required: isMandatoryCurrency }"
          v-on:update:model-value="$emit('update:currency', $event)"
        >
          {{ labelCurrency }}
          <template
            v-if="v$.fieldCurrency.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldCurrency.$errors" />
          </template>
        </DbSelectCustom>
      </div>
    </div>
    <div v-if="showPromocode">
      <RfpAlternativeField
        :forceOpen="promocode ? true : false"
        :labelAdd="labelAddPromocode"
        :labelRemove="labelRemovePromocode"
        v-on:remove="$emit('update:promocode', '')"
      >
        <DbInput
          :modelValue="promocode"
          v-on:update:model-value="$emit('update:promocode', $event)"
        >
          {{ labelPromocode }}
          <template
            v-if="v$.fieldPromocode.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldPromocode.$errors" />
          </template>
        </DbInput>
      </RfpAlternativeField>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, numeric, maxLength } from '@/utils/i18n-validators'
import type { Options } from '@/types/components/combobox'
import { getCurrencySign } from '@/utils/currency'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    /** Value of the field "Total Budget" */
    totalBudget?: string
    /** Value of the field "Currency" */
    currency?: string
    /** Value of the field "Promocode" */
    promocode?: string
    /** Label the field "Total Budget" */
    labelTotalBudget: string
    /** Label of the field "Currency" */
    labelCurrency: string
    /** Label of the field "Promocode" */
    labelPromocode: string
    /** Label of the button "Add Promocode" */
    labelAddPromocode: string
    /** Label of the button "Remove Promocode" */
    labelRemovePromocode: string
    /** List of currencies available */
    options: Options
    /** If "Total Budget" is mandatory */
    isMandatory: boolean
    /** If "Currency" is mandatory */
    isMandatoryCurrency: boolean
    /** If "Promocode" field is visible */
    showPromocode: boolean
  }>(),
  {
    totalBudget: '',
    currency: '',
    promocode: '',
  },
)

defineEmits<{
  (e: 'update:totalBudget', totalBudget: string): void
  (e: 'update:currency', currency: string): void
  (e: 'update:promocode', promocode: string): void
}>()

const { t } = useI18n()
const optionsWithEmptyValue = computed(() => {
  const defaultValue = {
    label: t('Select item'),
    value: '',
  }
  return [defaultValue, ...props.options]
})

const currencySign = computed(() => {
  if (!props.currency) {
    return false
  }
  return getCurrencySign(props.currency)
})

/**
 * Show currency select if there are more than one option of currency
 * and if modelData doesn't have a default currency
 */
const showCurrencySelect = computed(() => {
  if (!props.currency && props.options.length === 1) {
    return true
  }
  return props.options.length > 1
})

const state = computed(() => {
  return {
    fieldBudget: props.totalBudget,
    fieldCurrency: props.currency,
    fieldPromocode: props.promocode,
  }
})

const rules = computed(() => {
  return {
    fieldBudget: { required: requiredIf(props.isMandatory), numeric },
    fieldCurrency: { required: requiredIf(props.isMandatoryCurrency && showCurrencySelect.value) },
    fieldPromocode: { maxLengthValue: maxLength(50) },
  }
})

const v$ = useVuelidate(rules, state, { $autoDirty: true })
</script>
